import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import LoadingIndicator from '../../../components/ui/LoadingIndicator';

const ExcelUploadForm = ({ onFileUpload, uploadProgress, isProcessing }) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles?.length > 0) {
      setValidationErrors(['Please upload only Excel files (.xlsx, .xls)']);
      return;
    }

    const file = acceptedFiles?.[0];
    if (file) {
      setUploadedFile(file);
      setValidationErrors([]);
      onFileUpload(file);
    }
  }, [onFileUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  const downloadTemplate = () => {
    // Mock template download
    const templateData = `Role,Name,Character
Director,Christopher Nolan,
Producer,Emma Thomas,
Cast,Christian Bale,Bruce Wayne
Cast,Heath Ledger,The Joker
Cinematographer,Wally Pfister,
Editor,Lee Smith,`;

    const blob = new Blob([templateData], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'film-credits-template.csv';
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    window.URL?.revokeObjectURL(url);
  };

  const clearFile = () => {
    setUploadedFile(null);
    setValidationErrors([]);
  };

  return (
    <div className="space-y-6">
      {/* Template Download Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name="Download" size={24} className="text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">Download Template</h3>
            <p className="text-muted-foreground mb-4">
              Download our Excel template to ensure your credits are formatted correctly. The template includes sample data and proper column headers.
            </p>
            <Button
              variant="outline"
              iconName="FileSpreadsheet"
              iconPosition="left"
              onClick={downloadTemplate}
            >
              Download Excel Template
            </Button>
          </div>
        </div>
      </div>
      {/* File Upload Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Upload Credits File</h3>

        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
            isDragActive
              ? 'border-primary bg-primary/5'
              : uploadedFile
              ? 'border-success bg-success/5'
              : validationErrors?.length > 0
              ? 'border-error bg-error/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
          }`}
        >
          <input {...getInputProps()} />

          {isProcessing ? (
            <div className="space-y-4">
              <LoadingIndicator
                type="progress"
                progress={uploadProgress}
                message="Processing your file..."
                showMessage={true}
              />
            </div>
          ) : uploadedFile ? (
            <div className="space-y-4">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto">
                <Icon name="CheckCircle" size={32} className="text-success" />
              </div>
              <div>
                <h4 className="text-lg font-medium text-foreground">{uploadedFile?.name}</h4>
                <p className="text-sm text-muted-foreground">
                  {(uploadedFile?.size / 1024 / 1024)?.toFixed(2)} MB • Uploaded successfully
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="X"
                iconPosition="left"
                onClick={clearFile}
              >
                Remove File
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
                <Icon name="Upload" size={32} className="text-muted-foreground" />
              </div>
              <div>
                <h4 className="text-lg font-medium text-foreground">
                  {isDragActive ? 'Drop your file here' : 'Upload Excel File'}
                </h4>
                <p className="text-sm text-muted-foreground">
                  Drag and drop your Excel file here, or click to browse
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Supports .xlsx and .xls files up to 10MB
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Validation Errors */}
        {validationErrors?.length > 0 && (
          <div className="mt-4 p-4 bg-error/10 border border-error/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-error mb-1">Upload Error</h4>
                <ul className="text-sm text-error space-y-1">
                  {validationErrors?.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* File Format Guidelines */}
        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
            <Icon name="Info" size={16} />
            File Format Guidelines
          </h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Use the provided template for best results</li>
            <li>• Required columns: Role, Name</li>
            <li>• Optional columns: Character (for cast members)</li>
            <li>• Maximum 500 credits per file</li>
            <li>• Ensure all names are spelled correctly</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ExcelUploadForm;